import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/auth/AuthProvider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Activity,
  AlertTriangle,
  MapPin,
  Users,
  RefreshCw,
  ChevronRight,
  Camera,
} from "lucide-react";
import {
  getGpsAnalytics,
  getEmployeeOutOfRadius,
  type GpsAnalytics,
  type EmployeeOutOfRadiusDetails,
} from "@/utils/gps-analytics.functions";
import { getClockSelfieUrl } from "@/utils/clock-token.functions";
import { useServerFn } from "@tanstack/react-start";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { GpsViolationsMap } from "@/components/dashboard/GpsViolationsMap";

import { RequirePremium } from "@/components/subscription/RequirePremium";

export const Route = createFileRoute("/_app/gps-analytics")({
  component: GpsAnalyticsRouteComponent,
  head: () => ({
    meta: [{ title: "GPS Analytics — Business Dite Ads" }],
  }),
});

function GpsAnalyticsRouteComponent() {
  return (
    <RequirePremium featureKey="nav.gpsAnalytics">
      <GpsAnalyticsPage />
    </RequirePremium>
  );
}

function fmtMeters(n: number) {
  if (n >= 1000) return `${(n / 1000).toFixed(2)} km`;
  return `${n.toFixed(1)} m`;
}

function fmtDate(iso: string) {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function GpsAnalyticsPage() {
  const { t } = useTranslation();
  const { hasRole, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState("30");
  const [projectId, setProjectId] = useState<string>("__all__");
  const [data, setData] = useState<GpsAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  const RANGE_OPTIONS = [
    { value: "7", label: t("gpsAnalytics.ranges.d7") },
    { value: "30", label: t("gpsAnalytics.ranges.d30") },
    { value: "90", label: t("gpsAnalytics.ranges.d90") },
    { value: "180", label: t("gpsAnalytics.ranges.d180") },
  ];

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [details, setDetails] = useState<EmployeeOutOfRadiusDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [selfieUrl, setSelfieUrl] = useState<string | null>(null);
  const [selfieLoading, setSelfieLoading] = useState(false);
  const getSelfieUrlFn = useServerFn(getClockSelfieUrl);

  const openSelfie = async (path: string) => {
    setSelfieLoading(true);
    setSelfieUrl(null);
    try {
      const res = (await getSelfieUrlFn({ data: { path } })) as { url: string };
      setSelfieUrl(res.url);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("gpsAnalytics.errors.loadPhoto"));
      setSelfieUrl(null);
    } finally {
      setSelfieLoading(false);
    }
  };

  const isAuthorized = hasRole("admin") || hasRole("supervisor");

  useEffect(() => {
    if (!authLoading && !isAuthorized) {
      navigate({ to: "/dashboard" });
    }
  }, [authLoading, isAuthorized, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getGpsAnalytics({
        data: {
          days: Number(days),
          projectId: projectId === "__all__" ? null : projectId,
        },
      });
      setData(res);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("gpsAnalytics.errors.loadData"));
    } finally {
      setLoading(false);
    }
  };

  const openEmployee = async (userId: string) => {
    setSelectedUserId(userId);
    setDetails(null);
    setDetailsLoading(true);
    try {
      const res = await getEmployeeOutOfRadius({
        data: {
          userId,
          days: Number(days),
          projectId: projectId === "__all__" ? null : projectId,
        },
      });
      setDetails(res);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t("gpsAnalytics.errors.loadDetails"));
      setSelectedUserId(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthorized) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, projectId, isAuthorized]);

  if (!isAuthorized) return null;

  const outPct = data?.outPercent ?? 0;
  const pctTone =
    outPct >= 20
      ? "text-destructive"
      : outPct >= 10
        ? "text-amber-600 dark:text-amber-400"
        : "text-emerald-600 dark:text-emerald-400";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("gpsAnalytics.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("gpsAnalytics.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder={t("gpsAnalytics.selectProject")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t("gpsAnalytics.allProjects")}</SelectItem>
              {data?.projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={load} disabled={loading}>
            <RefreshCw className={loading ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("gpsAnalytics.kpis.attempts")}
            </CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.totalAttempts ?? 0}</div>
            <p className="text-xs text-muted-foreground">{t("gpsAnalytics.kpis.attemptsHint")}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("gpsAnalytics.kpis.outOfRadius")}
            </CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${pctTone}`}>{outPct.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              {t("gpsAnalytics.kpis.outOfTotal", {
                out: data?.outOfRadiusCount ?? 0,
                total: data?.totalAttempts ?? 0,
              })}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("gpsAnalytics.kpis.employees")}
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.uniqueEmployees ?? 0}</div>
            <p className="text-xs text-muted-foreground">
              {t("gpsAnalytics.kpis.projectsCount", { count: data?.uniqueProjects ?? 0 })}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("gpsAnalytics.kpis.maxDistance")}
            </CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{fmtMeters(data?.maxDistance ?? 0)}</div>
            <p className="text-xs text-muted-foreground">
              {t("gpsAnalytics.kpis.avg", { value: fmtMeters(data?.avgDistance ?? 0) })}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("gpsAnalytics.map.title")}</CardTitle>
          <p className="text-xs text-muted-foreground">{t("gpsAnalytics.map.legend")}</p>
        </CardHeader>
        <CardContent>
          {data &&
          (data.outAttemptsGeo.length > 0 ||
            data.inAttemptsGeo.length > 0 ||
            data.jobSites.length > 0) ? (
            <GpsViolationsMap
              pins={data.outAttemptsGeo}
              okPins={data.inAttemptsGeo}
              sites={data.jobSites}
            />
          ) : (
            <p className="text-sm text-muted-foreground">{t("gpsAnalytics.map.empty")}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("gpsAnalytics.byProject.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {data && data.byProject.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("gpsAnalytics.byProject.project")}</TableHead>
                  <TableHead className="text-right">
                    {t("gpsAnalytics.byProject.attempts")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("gpsAnalytics.byProject.outOfRadius")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("gpsAnalytics.byProject.outPercent")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("gpsAnalytics.byProject.worstOver")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.byProject.map((p) => {
                  const tone =
                    p.outPercent >= 20
                      ? "destructive"
                      : p.outPercent >= 10
                        ? "secondary"
                        : "outline";
                  return (
                    <TableRow key={p.projectId ?? "__none__"}>
                      <TableCell className="font-medium">{p.projectName}</TableCell>
                      <TableCell className="text-right">{p.total}</TableCell>
                      <TableCell className="text-right">{p.out}</TableCell>
                      <TableCell className="text-right">
                        <Badge variant={tone}>{p.outPercent.toFixed(1)}%</Badge>
                      </TableCell>
                      <TableCell className="text-right">{fmtMeters(p.maxOver)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">{t("gpsAnalytics.byProject.empty")}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("gpsAnalytics.worst.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {data && data.worstAttempts.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("gpsAnalytics.worst.rank")}</TableHead>
                  <TableHead>{t("gpsAnalytics.worst.employee")}</TableHead>
                  <TableHead>{t("gpsAnalytics.worst.project")}</TableHead>
                  <TableHead>{t("gpsAnalytics.worst.type")}</TableHead>
                  <TableHead>{t("gpsAnalytics.worst.when")}</TableHead>
                  <TableHead className="text-right">{t("gpsAnalytics.worst.distance")}</TableHead>
                  <TableHead className="text-right">{t("gpsAnalytics.worst.allowed")}</TableHead>
                  <TableHead className="text-right">{t("gpsAnalytics.worst.over")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.worstAttempts.map((a, i) => (
                  <TableRow
                    key={`${a.entry_id}-${a.kind}`}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => openEmployee(a.user_id)}
                  >
                    <TableCell>{i + 1}</TableCell>
                    <TableCell className="font-medium underline-offset-2 hover:underline">
                      {a.user_name}
                    </TableCell>
                    <TableCell>{a.project_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {a.kind === "in" ? t("gpsAnalytics.worst.in") : t("gpsAnalytics.worst.out")}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(a.at)}</TableCell>
                    <TableCell className="text-right">{fmtMeters(a.distance)}</TableCell>
                    <TableCell className="text-right">{fmtMeters(a.allowed)}</TableCell>
                    <TableCell className="text-right font-semibold text-destructive">
                      +{fmtMeters(a.over)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">{t("gpsAnalytics.worst.empty")}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("gpsAnalytics.recurring.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {data && data.recurringOffenders.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("gpsAnalytics.worst.employee")}</TableHead>
                  <TableHead className="text-right">
                    {t("gpsAnalytics.recurring.occurrences")}
                  </TableHead>
                  <TableHead className="text-right">
                    {t("gpsAnalytics.recurring.worstOver")}
                  </TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recurringOffenders.map((o) => (
                  <TableRow
                    key={o.userId}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => openEmployee(o.userId)}
                  >
                    <TableCell className="font-medium underline-offset-2 hover:underline">
                      {o.userName}
                    </TableCell>
                    <TableCell className="text-right">{o.outCount}</TableCell>
                    <TableCell className="text-right">{fmtMeters(o.maxOver)}</TableCell>
                    <TableCell className="text-right">
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground">{t("gpsAnalytics.recurring.empty")}</p>
          )}
        </CardContent>
      </Card>

      <Sheet
        open={selectedUserId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedUserId(null);
            setDetails(null);
          }
        }}
      >
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>
              {details?.userName ??
                (detailsLoading
                  ? t("gpsAnalytics.drilldown.loading")
                  : t("gpsAnalytics.drilldown.employee"))}
            </SheetTitle>
            <SheetDescription>{t("gpsAnalytics.drilldown.description")}</SheetDescription>
          </SheetHeader>

          {detailsLoading ? (
            <div className="mt-6 space-y-3">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-40 w-full" />
              <Skeleton className="h-60 w-full" />
            </div>
          ) : details ? (
            <div className="mt-6 space-y-6">
              <div className="grid grid-cols-2 gap-3">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-medium text-muted-foreground">
                      {t("gpsAnalytics.drilldown.totalOut")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{details.totalOut}</div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs font-medium text-muted-foreground">
                      {t("gpsAnalytics.drilldown.worstOver")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-destructive">
                      +{fmtMeters(details.maxOver)}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold">
                  {t("gpsAnalytics.drilldown.byProject")}
                </h3>
                {details.byProject.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("gpsAnalytics.byProject.project")}</TableHead>
                        <TableHead className="text-right">
                          {t("gpsAnalytics.recurring.occurrences")}
                        </TableHead>
                        <TableHead className="text-right">
                          {t("gpsAnalytics.recurring.worstOver")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {details.byProject.map((p) => (
                        <TableRow key={p.projectId ?? "__none__"}>
                          <TableCell className="font-medium">{p.projectName}</TableCell>
                          <TableCell className="text-right">{p.outCount}</TableCell>
                          <TableCell className="text-right">{fmtMeters(p.maxOver)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t("gpsAnalytics.drilldown.noData")}
                  </p>
                )}
              </div>

              <div>
                <h3 className="mb-2 text-sm font-semibold">
                  {t("gpsAnalytics.drilldown.attemptsCount", { count: details.attempts.length })}
                </h3>
                {details.attempts.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t("gpsAnalytics.worst.when")}</TableHead>
                        <TableHead>{t("gpsAnalytics.worst.project")}</TableHead>
                        <TableHead>{t("gpsAnalytics.worst.type")}</TableHead>
                        <TableHead className="text-right">
                          {t("gpsAnalytics.worst.distance")}
                        </TableHead>
                        <TableHead className="text-right">{t("gpsAnalytics.worst.over")}</TableHead>
                        <TableHead className="text-right">
                          {t("gpsAnalytics.drilldown.photo")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {details.attempts.map((a) => (
                        <TableRow key={`${a.entry_id}-${a.kind}`}>
                          <TableCell className="text-xs text-muted-foreground">
                            {fmtDate(a.at)}
                          </TableCell>
                          <TableCell>{a.project_name}</TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {a.kind === "in"
                                ? t("gpsAnalytics.worst.in")
                                : t("gpsAnalytics.worst.out")}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">{fmtMeters(a.distance)}</TableCell>
                          <TableCell className="text-right font-semibold text-destructive">
                            +{fmtMeters(a.over)}
                          </TableCell>
                          <TableCell className="text-right">
                            {a.selfie_path ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 gap-1 px-2"
                                onClick={() => void openSelfie(a.selfie_path!)}
                              >
                                <Camera className="h-3.5 w-3.5" />
                                {t("gpsAnalytics.drilldown.view")}
                              </Button>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t("gpsAnalytics.drilldown.noAttempts")}
                  </p>
                )}
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      <Dialog
        open={selfieUrl !== null || selfieLoading}
        onOpenChange={(open) => {
          if (!open) {
            setSelfieUrl(null);
            setSelfieLoading(false);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-4 w-4 text-primary" />
              {t("gpsAnalytics.drilldown.selfieTitle")}
            </DialogTitle>
          </DialogHeader>
          {selfieLoading || !selfieUrl ? (
            <Skeleton className="h-80 w-full" />
          ) : (
            <img
              src={selfieUrl}
              alt={t("gpsAnalytics.drilldown.selfieAlt")}
              className="block max-h-[70vh] w-full rounded-md border border-border bg-muted object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
